import { type ReactNode, createContext, useCallback, useContext, useState } from "react";
import {
  type IModalConfirmationOptions,
  type IModalConfirmationProps,
  type IModalConfirmationSlotProps,
  ModalConfirmation,
} from "./modal";

const Context = createContext<
  (
    title: ReactNode,
    message: ReactNode,
    options?: IModalConfirmationOptions,
    slotProps?: IModalConfirmationSlotProps
  ) => Promise<boolean>
>(() => new Promise<boolean>((resolve) => resolve(false)));

export const useGlobalModalConfirmation = () => useContext(Context);

export interface IGlobalModalConfirmationProps {
  children: ReactNode | ReactNode[];
}

const initialModalState = {
  open: false,
  resolve: () => undefined,
  title: "",
  message: "",
} as const satisfies IModalConfirmationProps;

export const GlobalModalConfirmation = ({ children }: IGlobalModalConfirmationProps) => {
  const [modalState, setModalState] = useState<IModalConfirmationProps>(initialModalState);

  const confirm = useCallback(
    (
      title: ReactNode,
      message: ReactNode,
      options?: IModalConfirmationOptions,
      slotProps?: IModalConfirmationSlotProps
    ) => {
      return new Promise<boolean>((resolve) => {
        setModalState({
          open: true,
          resolve: (result: boolean) => {
            resolve(result);
            setModalState((state) => ({ ...state, open: false, resolve: () => undefined }));
          },
          title,
          message,
          options,
          slotProps,
        });
      });
    },
    []
  );

  return (
    <Context.Provider value={confirm}>
      {children}
      <ModalConfirmation {...modalState} />
    </Context.Provider>
  );
};
